'use client';

/**
 * API namespace `bundles` (sub-namespace within parent).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L8283-L8403).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-4, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const bundles = {
  /**
   * GET /api/market/bundles — paginated public bundle catalog.
   *
   * Returns `ProductBundleListResponse` (`bundles:
   * ProductBundleResponse[]` + `total` + `page` + `limit`). The backend
   * clamps `page >= 1` and `limit` to `[1, 100]` (defense in depth). The
   * backend independently filters by `status='approved'` + `active=true`
   * + active seller (INV-MKT-20 — admin-approval gate; pending/
   * rejected/paused/out_of_stock/soft-deleted bundles + bundles from
   * inactive sellers are hidden from the public catalog).
   *
   * Authz: PUBLIC (no auth required). 403 `MARKET_PRODUCTS_DISABLED`
   * when the `market_products_enabled` feature flag is off (bundles
   * inherit the products gate — they're a strict superset).
   *
   * Spec ref: SPEC-242 §4.4 (GET /market/bundles), §4.5.2 (catálogo
   *           público UI).
   */
  async list(
    filters: {
      page?: number;
      limit?: number;
      search?: string;
      minPrice?: string;
      maxPrice?: string;
      sortBy?: 'newest' | 'price_low' | 'price_high';
    },
    signal?: AbortSignal,
  ): Promise<ProductBundleListResponse> {
    const qs = new URLSearchParams();
    if (filters.page !== undefined) qs.set('page', String(filters.page));
    if (filters.limit !== undefined) qs.set('limit', String(filters.limit));
    if (filters.search && filters.search.trim().length > 0) {
      qs.set('search', filters.search.trim());
    }
    if (filters.minPrice) qs.set('minPrice', filters.minPrice);
    if (filters.maxPrice) qs.set('maxPrice', filters.maxPrice);
    if (filters.sortBy) qs.set('sortBy', filters.sortBy);
    const query = qs.toString();
    return apiFetch<ProductBundleListResponse>(
      query ? `/market/bundles?${query}` : '/market/bundles',
      { auth: false, signal },
    );
  },
  /**
   * GET /api/market/bundles/:storeSlug/:bundleSlug — public bundle
   * detail.
   *
   * Returns `ProductBundleResponse` — a privacy-filtered view of the
   * bundle (NO `sellerId` is exposed at the wire level beyond what the
   * snapshot carries; the response includes `sellerStoreName` +
   * `sellerStoreSlug` so the detail page can render the seller link
   * without an extra `getPublicStore` roundtrip). The response also
   * includes the expanded `products[]` snapshot (`productId`, `title`,
   * `priceCredits`, `images`) so the detail page can render the
   * "Productos incluidos" section without N+1 product lookups.
   *
   * Authz: PUBLIC. 404 if the bundle does not exist OR is not
   * `status='approved'` OR `active=false` OR the seller is inactive
   * (the backend hides pending/rejected/paused/out_of_stock/soft-
   * deleted bundles + bundles from inactive sellers — INV-MKT-20).
   * 403 `MARKET_PRODUCTS_DISABLED` when the flag is off.
   *
   * Spec ref: SPEC-242 §4.4 (GET /market/bundles/:storeSlug/:bundleSlug),
   *           §4.5.2 (catálogo público UI — detalle del combo).
   */
  async getBySlug(
    storeSlug: string,
    bundleSlug: string,
    signal?: AbortSignal,
  ): Promise<ProductBundleResponse> {
    return apiFetch<ProductBundleResponse>(
      `/market/bundles/${encodeURIComponent(storeSlug)}/${encodeURIComponent(bundleSlug)}`,
      { auth: false, signal },
    );
  },
};
